import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
} from "@mui/material";

type ConclusionModalProps = {
  open: boolean;
  onClose: () => void;
  conclusion: any;
};

const ConclusionModal: React.FC<ConclusionModalProps> = ({
  open,
  onClose,
  conclusion,
}) => {
  const [parsedConclusion, setParsedConclusion] = useState<any>(null);

  useEffect(() => {
    if (!conclusion) {
      setParsedConclusion(null);
      return;
    }

    try {
      const parsed =
        typeof conclusion === "string" ? JSON.parse(conclusion) : conclusion;
      setParsedConclusion(parsed);
    } catch (err) {
      console.error("Invalid conclusion JSON", err);
      setParsedConclusion(null);
    }
  }, [conclusion]);

  const formatPatientMadeDate = (dateStr: string) => {
    if (!dateStr) return "-";

    const date = new Date(dateStr.replace(" ", "T"));

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ style: { borderRadius: 10 } }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          backgroundColor: "#194e5b",
          color: "white",
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          fontSize: 18,
          fontWeight: 600,
          py: 1.5,
          position: "relative",
        }}
      >
        Conclusion Details
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
          }}
        >
          <i className="bi bi-x-lg" style={{ fontSize: "18px" }}></i>
        </IconButton>
      </DialogTitle>

      {/* Body */}
      <DialogContent sx={{ p: 2 }}>
        {!parsedConclusion ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, textAlign: "center" }}
          >
            No conclusion data available
          </Typography>
        ) : (
          <Box
            sx={{
              maxHeight: "350px",
              overflowY: "auto",
              mt: 1,
              pr: 1,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#ccc",
                borderRadius: "4px",
              },
            }}
          >
            {Object.entries(parsedConclusion).map(([key, value]) => (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  backgroundColor: "#f8f9f9",
                  borderRadius: "8px",
                  padding: "10px 12px",
                  mb: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "#0E3843",
                    textTransform: "capitalize",
                    maxWidth: "45%",
                  }}
                >
                  {key.replace(/_/g, " ")}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "#0E3843",
                    textAlign: "right",
                    maxWidth: "55%",
                    wordBreak: "break-word",
                  }}
                >
                  {key === "patient_made_date"
                    ? formatPatientMadeDate(String(value))
                    : String(value)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConclusionModal;
